// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// installerStopDistance — #205 (Brandon). Gate the multi-stop recommender so a
// 2nd-stop job/repair is only recommended when it's within the installer's
// per-installer travel tolerance (Recruiter field `StopDistance`).
//
// Brandon's decisions (2026-06-16):
//   - Distance method = "Option A+": straight-line haversine PADDED by a
//     ~1.3 road factor (no Google API — free, instant, runs on every render).
//   - Measured JOB-TO-JOB: the installer's already-assigned stop(s) that day →
//     each recommended 2nd-stop job/repair (nearest assigned stop wins).
//   - `StopDistance` buckets: 10 / 20 / 30 / 40 / 50 (miles); "51+" (and
//     missing/blank) = NO LIMIT and is the default.
//   - Applies to BOTH filler jobs and repair 2nd-stops.
//
// SAFE-DEFAULT / FAIL-OPEN (same pattern as capacity_stops_* #146 /
// capacity_stores #149 / labor-attr gate #194): when we can't measure
// (no limit configured, candidate not geocoded, or the installer has no
// geocoded assigned stop that day) the gate returns TRUE — it never HIDES a
// recommendation it can't evaluate. So until Recruiter populates `StopDistance`
// on real installers AND the job geocoding (`google_lat`/`google_lon`)
// backfills, this gate is inert and the recommender behaves exactly as today.
// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
import { haversineMiles } from "./haversineMiles";

// Option A+ road factor — pads straight-line miles toward real road miles.
// A 38-straight-line-mile candidate counts as ~49 road miles (passes a "50"
// installer); a 40-mile one counts as 52 (fails "50"). Tunable in one place.
export const STOP_DISTANCE_ROAD_FACTOR = 1.3;

// Parse the per-installer `StopDistance` bucket → a mileage cap.
//   "10"|"20"|"30"|"40"|"50" → that many miles.
//   "51+" (any "+"-suffixed bucket), "", null, undefined, 0, or non-numeric
//   → Infinity (no limit; the default).
export function parseStopDistanceLimitMiles(value) {
  if (value === null || value === undefined) return Infinity;
  const s = String(value).trim();
  if (s === "") return Infinity;
  if (s.includes("+")) return Infinity; // "51+" top bucket = unlimited
  const n = parseFloat(s);
  if (!Number.isFinite(n) || n <= 0) return Infinity;
  return n;
}

// #205 (Brandon, 2026-06-17) — human-readable label for the per-installer
// `StopDistance` bucket, shown READ-ONLY in CrewDetailModal → Capacities tab so
// Leland + Brandon can SEE the configured value while testing. Ties to the
// gate's own parser so the "no limit" buckets ("51+", blank, 0, non-numeric)
// read consistently with how the recommender actually treats them.
export function formatStopDistanceLabel(value) {
  const s = value === null || value === undefined ? "" : String(value).trim();
  if (s === "") return "Not set — no distance limit (default)";
  if (!Number.isFinite(parseStopDistanceLimitMiles(value)))
    return `${s} — no distance limit`;
  return `${s} miles`;
}

// Extract validated coordinates from a job/candidate ActiveQueue doc
// (geocoded `google_lat`/`google_lon`). Returns {lat, lon} or null when
// absent/invalid → the gate fails open.
export function getJobCoords(doc) {
  if (!doc) return null;
  const lat = Number(doc.google_lat);
  const lon = Number(doc.google_lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (lat === 0 && lon === 0) return null; // null-island guard (un-geocoded sentinel)
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
  return { lat, lon };
}

// #205 (Brandon, 2026-06-17) — read-only "lat, lon" label for the geocoded
// coordinates on a job doc, shown in JobsDetailModal so Leland + Brandon can SEE
// whether/where a job is geocoded while testing. Reuses getJobCoords so the
// "not geocoded" determination matches exactly what the recommender gate sees
// (un-geocoded sentinels / out-of-range coords → "not geocoded"). Rounded to 5
// decimals (~1 m) for readability.
export function formatJobCoordsLabel(doc) {
  const c = getJobCoords(doc);
  if (!c) return "— not geocoded —";
  const round = (n) => Math.round(n * 1e5) / 1e5;
  return `${round(c.lat)}, ${round(c.lon)}`;
}

// The estimated ROAD distance (straight-line × roadFactor, Option A+) from the
// installer's NEAREST already-assigned stop that day to a candidate 2nd-stop
// job/repair. Returns the minimum across all assigned stops ("nearest assigned
// stop wins" — the same rule the gate uses), or `null` when unmeasurable
// (candidate not geocoded, or the installer has no geocoded assigned stop).
//
// #205 (Brandon, 2026-06-17) — also surfaced on each Capacity Recommendations
// popover row so the install manager can SEE the mileage for a possible move-up.
// Because the gate (`installerCanReachSecondStop`) is now derived from this
// same number, the displayed mileage and the pass/fail decision can never drift.
export function nearestStopRoadMiles(
  firstStopCoords,
  candidateDoc,
  roadFactor = STOP_DISTANCE_ROAD_FACTOR
) {
  const cand = getJobCoords(candidateDoc);
  if (!cand) return null; // candidate not geocoded → can't measure

  const valid = Array.isArray(firstStopCoords)
    ? firstStopCoords.filter(Boolean)
    : [];
  if (valid.length === 0) return null; // no geocoded assigned stop → can't measure

  let min = Infinity;
  for (const fc of valid) {
    const miles = haversineMiles(fc.lat, fc.lon, cand.lat, cand.lon);
    if (Number.isFinite(miles)) {
      const road = miles * roadFactor;
      if (road < min) min = road;
    }
  }
  return Number.isFinite(min) ? min : null;
}

// The #205 gate. TRUE = recommend (within range, or can't-measure fail-open).
//
//   stopDistanceValue — the installer's `StopDistance` (e.g. "10", "51+", "").
//   firstStopCoords   — [{lat,lon}, ...] of the installer's already-assigned
//                       stops that day (the "first stops"). Empty → fail open.
//   candidateDoc      — the recommended 2nd-stop job/repair (carries google_*).
//   roadFactor        — straight-line → road padding (default 1.3, Option A+).
//
// Constrains ONLY when there's a finite limit AND coords on both sides; then
// the candidate must be within `limit` ROAD-miles of the NEAREST assigned stop.
export function installerCanReachSecondStop({
  stopDistanceValue,
  firstStopCoords,
  candidateDoc,
  roadFactor = STOP_DISTANCE_ROAD_FACTOR,
}) {
  const limit = parseStopDistanceLimitMiles(stopDistanceValue);
  if (!Number.isFinite(limit)) return true; // no limit (51+/missing) → no constraint

  const miles = nearestStopRoadMiles(firstStopCoords, candidateDoc, roadFactor);
  if (miles === null) return true; // can't measure → fail open

  return miles <= limit;
}

// Convenience used by the recommenders: collect the geocoded coordinates of an
// installer's already-assigned stops on a given cell day, from the full
// ActiveQueue list. `normalize` formats the candidate payrollNo to the
// ActiveQueue `installer` string form; `datesEqual` is the semantic day match.
export function collectAssignedStopCoords(
  activeQueueDocs,
  installerPayroll,
  cellDate,
  normalize,
  datesEqual
) {
  if (!Array.isArray(activeQueueDocs)) return [];
  const out = [];
  for (const d of activeQueueDocs) {
    if (!d) continue;
    if (normalize(d.installer) !== installerPayroll) continue;
    if (parseFloat(d.statusCode) === 20) continue; // exclude Cancelled
    if (!datesEqual(d.date_scheduled, cellDate)) continue;
    const c = getJobCoords(d);
    if (c) out.push(c);
  }
  return out;
}
